/**
 * Drehbuch „Aivy Spalten“: drei Rollen, ein Prozess. Jeder Schritt gehört
 * einer Spalte, die anderen beiden zeigen, was sie in dem Moment tun.
 * Zahlen und Zitate wie im Demo-Deck, Namen und Werte sind Beispieldaten.
 */

export const LANES = [
  {
    id: 'team',
    title: 'Fachabteilung',
    person: 'Tim K.',
    role: 'Teamleitung Vertrieb'
  },
  { id: 'hr', title: 'HR', person: 'Sara B.', role: 'Recruiting' },
  { id: 'talent', title: 'Talent', person: 'Lena M.', role: 'Bewerberin' }
]

// Vorteile als Punkte, jede Kategorie mit einer Aivy-Form
export const POINTS = {
  auto: { label: 'Automatisiert', color: '#4461D4', shape: 'circle' },
  fair: { label: 'Fairer', color: '#29CCC7', shape: 'square' },
  fast: { label: 'Schneller', color: '#FFB628', shape: 'tri' },
  data: { label: 'Datenbasiert', color: '#FE79A7', shape: 'half' }
}

export const STEPS = [
  {
    id: 'bedarf',
    lane: 'team',
    title: 'Bedarf melden',
    text: 'Das Team wächst, eine Stelle muss besetzt werden. Tim schreibt kurz an HR.',
    ui: 'message',
    idle: {
      hr: 'Bekommt die Anfrage',
      talent: 'Sucht gerade einen neuen Job'
    },
    points: []
  },
  {
    id: 'stelle',
    lane: 'hr',
    title: 'Stelle anlegen',
    text: 'Sara legt die Stelle in Aivy an. Name, Ort, fertig.',
    ui: 'newJob',
    idle: { team: 'Arbeitet normal weiter', talent: 'Noch nicht beteiligt' },
    points: [['fast', 'In wenigen Minuten angelegt']]
  },
  {
    id: 'weg',
    lane: 'hr',
    title: 'Anforderungen klären',
    text: 'Sara entscheidet, wie das Anforderungsprofil entsteht. Mit einer Aivy-Vorlage sofort, oder zusätzlich mit dem Team.',
    ui: 'choice',
    idle: {
      team: 'Wird auf Wunsch eingebunden',
      talent: 'Noch nicht beteiligt'
    },
    points: [['fast', 'Vorlage ist sofort startklar']]
  },
  {
    id: 'experten',
    lane: 'team',
    optional: true,
    title: 'Kolleg:innen befragen',
    text: 'Tim und zwei Kolleg:innen schätzen unabhängig voneinander ein, was die Stelle wirklich braucht. 5 bis 10 Minuten pro Person.',
    ui: 'expert',
    idle: {
      hr: 'Sieht die Antworten eintreffen',
      talent: 'Noch nicht beteiligt'
    },
    points: [
      ['fair', 'Mehrere Perspektiven statt einer Meinung'],
      ['data', 'Erfahrung aus dem Team wird messbar']
    ]
  },
  {
    id: 'profil',
    lane: 'hr',
    title: 'Anforderungsprofil steht',
    text: 'Aus Vorlage und Einschätzungen entsteht das Profil. Ideal- und Übergangsbereich je Dimension.',
    ui: 'profile',
    idle: {
      team: 'Kann das Profil jederzeit ansehen',
      talent: 'Noch nicht beteiligt'
    },
    points: [
      ['fair', 'Gleiche Messlatte für alle, nach DIN 33430'],
      ['data', 'Steht, bevor die erste Bewerbung kommt']
    ]
  },
  {
    id: 'einladen',
    lane: 'hr',
    title: 'Talente einladen',
    text: 'Jede Bewerbung bekommt den Einladungslink. Über euer ATS passiert das automatisch beim Statuswechsel.',
    ui: 'invite',
    idle: { team: 'Muss nichts tun', talent: 'Bekommt die Einladung' },
    points: [['auto', 'Einladung automatisch über euer ATS']]
  },
  {
    id: 'spielen',
    lane: 'talent',
    title: 'Assessment spielen',
    text: 'Lena löst kurze, spielbasierte Aufgaben am Smartphone. Ohne Vorbereitung und ohne Lebenslauf.',
    ui: 'challenge',
    idle: {
      team: 'Muss nichts tun',
      hr: 'Muss nichts tun, die Ergebnisse kommen automatisch'
    },
    points: [
      ['fair', 'Gleiche Bedingungen, ohne Lebenslauf'],
      ['fast', '2 bis 5 Minuten je Spiel']
    ]
  },
  {
    id: 'staerken',
    lane: 'talent',
    title: 'Eigenes Stärkenprofil',
    text: 'Lena bekommt ihr Stärkenprofil direkt zurück, egal wie die Entscheidung ausfällt.',
    ui: 'strength',
    idle: { team: 'Muss nichts tun', hr: 'Ergebnis ist da' },
    points: [['fair', 'Jede:r bekommt ein Ergebnis zurück']]
  },
  {
    id: 'passung',
    lane: 'hr',
    title: 'Passung ansehen',
    text: 'Alle Talente sind schon gegen das Anforderungsprofil gerechnet. Sara sieht sofort, wer gut passt.',
    ui: 'table',
    idle: {
      team: 'Bekommt gleich die Auswahl',
      talent: 'Wartet auf Rückmeldung'
    },
    points: [
      ['auto', 'Automatisch gegen das Profil gerechnet'],
      ['fast', '70 % weniger Aufwand in der Vorauswahl (Callways)']
    ]
  },
  {
    id: 'interview',
    lane: 'team',
    title: 'Interview vorbereiten',
    text: 'Tim bekommt zu jedem Talent Hinweise, was er im Gespräch vertiefen sollte.',
    ui: 'insights',
    idle: {
      hr: 'Plant die Interviews',
      talent: 'Ist zum Interview eingeladen'
    },
    points: [
      ['fair', 'Strukturiertes Interview, für alle gleich'],
      ['data', 'Das Gespräch klärt offene Punkte, nicht die Grundlagen']
    ]
  },
  {
    id: 'entscheidung',
    lane: 'hr',
    title: 'Einstellen',
    text: 'Nach dem Interview entscheidet das Team, Sara setzt den Status in Aivy.',
    ui: 'hire',
    idle: {
      team: 'Hat sich für Lena entschieden',
      talent: 'Bekommt die Zusage'
    },
    points: [['fast', '55 % kürzere Time-to-Hire (MCI)']]
  },
  {
    id: 'lernen',
    lane: 'hr',
    later: true,
    title: 'Auf Dauer: Aivy lernt mit',
    text: 'Nach der Probezeit setzt Sara den nächsten Status. Mit dem Add-on SmartPredict lernen Anforderungen und Matching aus euren Einstellungen.',
    ui: 'learn',
    idle: { team: 'Lena ist fest im Team', talent: 'Ist angekommen' },
    points: [
      ['data', 'Jede Einstellung macht das nächste Matching treffsicherer'],
      ['auto', 'Profile aktualisieren sich aus euren Daten']
    ]
  },
  {
    id: 'team',
    lane: 'team',
    later: true,
    title: 'Team im Blick',
    text: 'In der Teamanalyse sieht Tim die Stärken seines Teams. Die nächste Stelle startet mit diesem Wissen.',
    ui: 'radar',
    idle: {
      hr: 'Startet die nächste Stelle schneller',
      talent: 'Wächst im Team'
    },
    points: [['data', 'Stärken im Team sichtbar, für die nächste Stelle']]
  }
]

// Daten, die zwischen den Spalten wandern
export const FLOWS = [
  { from: 'bedarf', to: 'stelle', label: 'Anfrage' },
  { from: 'experten', to: 'profil', label: 'Einschätzungen' },
  { from: 'einladen', to: 'spielen', label: 'Einladung' },
  { from: 'staerken', to: 'passung', label: 'Ergebnisse' },
  { from: 'passung', to: 'interview', label: 'Auswahl und Hinweise' },
  { from: 'interview', to: 'entscheidung', label: 'Entscheidung' },
  { from: 'lernen', to: 'team', label: 'Teamdaten' }
]
